import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Collapse, Skeleton, Stack, Typography } from '@mui/material';
import RadarIcon from '@mui/icons-material/Radar';
import { resources } from '../../../core/services';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { IconTile } from '../../../shared/components/IconTile';
import { LiveDot } from '../../../shared/components/LiveDot';
import { Pill } from '../../../shared/components/Pill';
import { SectionCollapseButton, useSectionCollapse } from '../../../shared/components/SectionCollapseButton';
import type { EntityMarketFeed, EntityMarketFeedImportance, Job } from '../../../core/types/data-models';
import { MarketRadarEntry, RAIL_CENTER_PX, RAIL_GUTTER_PX } from './MarketRadarEntry';

// ── Market Radar (project dashboard block) ───────────────────────────────────
//
// Every EntityMarketFeed row of the project as one vertical timeline: newest
// day first, highest-importance signal first within a day. Each entry links
// into ManagedEntityTypeDetail's "Market Feed" tab (`?tab=market-feed`) for the
// entity type it belongs to, so the full feed is one click away.
//
// Rows are produced per (projectId, entityType) by the
// GENERATE_ENTITY_MARKET_FEED job, run from that same view.

const MARKET_FEED_JOB_TYPE = 'GENERATE_ENTITY_MARKET_FEED';
const DAY_MS = 24 * 60 * 60 * 1000;

/** Order within one day — a high-impact signal leads, whatever the hour. */
const IMPORTANCE_RANK: Record<EntityMarketFeedImportance, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 };

function startOfDay(iso: string): number {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function formatDayLabel(dayStart: number): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((today.getTime() - dayStart) / DAY_MS);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(dayStart).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

interface FeedDay {
  dayStart: number;
  label: string;
  items: EntityMarketFeed[];
}

/** Group the feed into day buckets, newest day first, most important signal first inside a day. */
function groupByDay(items: EntityMarketFeed[]): FeedDay[] {
  const sorted = [...items].sort((a, b) => {
    const byDay = startOfDay(b.publishedAt) - startOfDay(a.publishedAt);
    if (byDay !== 0) return byDay;
    const byImportance = IMPORTANCE_RANK[a.importance] - IMPORTANCE_RANK[b.importance];
    if (byImportance !== 0) return byImportance;
    return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
  });

  const days: FeedDay[] = [];
  for (const item of sorted) {
    const dayStart = startOfDay(item.publishedAt);
    const current = days[days.length - 1];
    if (current && current.dayStart === dayStart) current.items.push(item);
    else days.push({ dayStart, label: formatDayLabel(dayStart), items: [item] });
  }
  return days;
}

interface MarketRadarSectionProps {
  projectId: number;
  /** The project's PENDING/PROCESSING jobs — ProjectDetail already polls these every few seconds. */
  activeJobs: Job[];
  /** Height cap for the dashboard row this block shares; the timeline scrolls inside it. */
  maxHeight?: number;
}

export function MarketRadarSection({ projectId, activeJobs, maxHeight }: MarketRadarSectionProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expanded, toggleExpanded] = useSectionCollapse('marketRadar');

  const feedQuery = useQuery({
    queryKey: ['project-market-feed', projectId],
    queryFn: () =>
      resources.entityMarketFeeds.list({
        filter: { projectId: [projectId] },
        sort: '-publishedAt',
        pageSize: 200,
      }),
    enabled: Number.isFinite(projectId),
  });
  const items = useMemo(() => feedQuery.data?.data ?? [], [feedQuery.data]);
  const days = useMemo(() => groupByDay(items), [items]);
  const highCount = items.filter((i) => i.importance === 'HIGH').length;

  // Which entity types are being researched right now, read off the project's
  // active jobs so the state survives reloads and other tabs.
  const refreshingTypes = useMemo(() => {
    const types = new Set<string>();
    for (const job of activeJobs) {
      if (job.jobType !== MARKET_FEED_JOB_TYPE || !job.data) continue;
      try {
        const data = JSON.parse(job.data) as { entityType?: string };
        if (data.entityType) types.add(data.entityType);
      } catch {
        // Malformed job data — nothing to mark as refreshing.
      }
    }
    return types;
  }, [activeJobs]);

  // A run that drops out of the active set has finished (or failed) and has
  // replaced that type's rows, so the timeline is refetched.
  const prevRefreshingCount = useRef(0);
  useEffect(() => {
    if (refreshingTypes.size < prevRefreshingCount.current) {
      queryClient.invalidateQueries({ queryKey: ['project-market-feed', projectId] });
    }
    prevRefreshingCount.current = refreshingTypes.size;
  }, [refreshingTypes, queryClient, projectId]);

  const openMarketFeed = (entityType: string) => {
    navigate(`/projects/${projectId}/managed-knowledge/${encodeURIComponent(entityType)}?tab=market-feed`);
  };

  return (
    // Stretching to the row makes this and the Project Key Entities block
    // beside it the same height; the cap keeps the row from growing past it.
    // Pinned to the second column so the radar keeps its third even while that
    // block is still loading (it renders null).
    <GlassPanel
      sx={{
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        gridColumn: { lg: '2 / 3' },
        maxHeight: expanded ? maxHeight : undefined,
        alignSelf: expanded ? 'stretch' : 'start',
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        flexWrap="wrap"
        sx={{ mb: expanded ? 2 : 0, rowGap: 1, flexShrink: 0 }}
      >
        <IconTile icon={<RadarIcon fontSize="inherit" />} color="cyan" size="sm" />
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
            Market Radar
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Recent signals about the entities you track, newest first
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
          {refreshingTypes.size > 0 && (
            <Pill color="success" mono={false}>
              <LiveDot size={6} /> Scanning…
            </Pill>
          )}
          {items.length > 0 && (
            <>
              <Pill>{items.length} signal{items.length === 1 ? '' : 's'}</Pill>
              {highCount > 0 && <Pill color="error">{highCount} high</Pill>}
            </>
          )}
        </Stack>
        <SectionCollapseButton expanded={expanded} onToggle={toggleExpanded} label="Market Radar" />
      </Stack>

      {/* The scroll region is a direct flex child of the capped panel, so its
          height is bounded without anything being threaded through Collapse. */}
      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', pr: 0.75 }}>
        <Collapse in={expanded}>
          {feedQuery.isPending ? (
            <Stack spacing={1}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rounded" height={104} />
              ))}
            </Stack>
          ) : items.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              No market signals yet. Open a managed entity type and run "Find Market News" to collect the past
              week's coverage of its entities here.
            </Typography>
          ) : (
            <Box sx={{ position: 'relative', pl: `${RAIL_GUTTER_PX}px` }}>
              {/* The rail itself — inside the scrolled content so it spans the whole timeline. */}
              <Box
                sx={{
                  position: 'absolute',
                  left: RAIL_CENTER_PX - 1,
                  top: 8,
                  bottom: 8,
                  width: 2,
                  borderRadius: 1,
                  bgcolor: 'divider',
                }}
              />
              {days.map((day) => (
                <Box key={day.dayStart} sx={{ '&:not(:last-of-type)': { mb: 1.75 } }}>
                  <Box sx={{ position: 'relative', mb: 0.75 }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        left: RAIL_CENTER_PX - 4 - RAIL_GUTTER_PX,
                        top: 4,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'text.disabled',
                      }}
                    />
                    <Typography variant="overline" color="text.secondary" lineHeight={1.6}>
                      {day.label}
                    </Typography>
                  </Box>
                  <Stack spacing={1}>
                    {day.items.map((item) => (
                      <MarketRadarEntry
                        key={item.id}
                        item={item}
                        onOpen={() => openMarketFeed(item.entityType)}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}
            </Box>
          )}
        </Collapse>
      </Box>
    </GlassPanel>
  );
}
